import React, { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { Trans, useTranslation } from "react-i18next";
import { getValueFromInput } from "util/FormsUtils";
import {
  Col,
  Input,
  Button,
  ControlLabel,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
  Row
} from "components/bootstrap";
import { ColorPickerPopover, TimezoneSelect } from "components/common";
import ColorLabel from "components/sidecars/common/ColorLabel";
import DocumentationLink from "components/support/DocumentationLink";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
function EventProcedureCheckbox({ checked, onChange, validation }) {
  var _a, _b;
  const { t } = useTranslation("integrations");
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  if (!validSecurityLicense) return null;
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "include_event_procedure",
      name: "include_event_procedure",
      label: t("event_procedure_checkbox.label"),
      help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.include_event_procedure) == null ? void 0 : _b[0]) || t("event_procedure_checkbox.help"),
      type: "checkbox",
      checked,
      onChange
    }
  );
}
export const defaultConfig = {
  color: "#FF0000",
  webhook_url: "",
  channel: "#channel",
  /* eslint-disable no-template-curly-in-string */
  custom_message: "--- [Event Definition] ---------------------------\nTitle:       ${event_definition_title}\nType:        ${event_definition_type}\n--- [Event] --------------------------------------\nAlert Replay:         ${http_external_uri}alerts/${event.id}/replay-search\nTimestamp:            ${event.timestamp}\nMessage:              ${event.message}\nSource:               ${event.source}\nKey:                  ${event.key}\nPriority:             ${event.priority}\nAlert:                ${event.alert}\nTimestamp Processing: ${event.timestamp}\nTimerange Start:      ${event.timerange_start}\nTimerange End:        ${event.timerange_end}\nEvent Fields:\n${foreach event.fields field}\n${field.key}: ${field.value}\n${end}\n${if backlog}\n--- [Backlog] ------------------------------------\nLast messages accounting for this alert:\n${foreach backlog message}\n${message.timestamp}  ::  ${message.source}  ::  ${message.message}\n${message.message}\n${end}${end}\n",
  /* eslint-enable no-template-curly-in-string */
  user_name: "Username",
  notify_channel: false,
  link_names: "",
  icon_url: "",
  icon_emoji: "",
  backlog_size: 0,
  time_zone: "UTC",
  include_title: void 0,
  notify_here: void 0,
  include_event_procedure: false
};
const SlackNotificationForm = ({ config, validation, onChange }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v;
  const { t } = useTranslation("integrations");
  const defaultBacklogSize = config.backlog_size;
  const [isBacklogSizeEnabled, setIsBacklogSizeEnabled] = useState(defaultBacklogSize > 0);
  const [backlogSize, setBacklogSize] = useState(defaultBacklogSize);
  const propagateChange = (key, value) => {
    const nextConfig = cloneDeep(config);
    nextConfig[key] = value;
    onChange(nextConfig);
  };
  const handleBacklogSizeChange = (event) => {
    const { name } = event.target;
    const value = Number(getValueFromInput(event.target));
    setBacklogSize(value);
    propagateChange(name, value);
  };
  const toggleBacklogSize = () => {
    setIsBacklogSizeEnabled((prevIsBacklogSizeEnabled) => {
      const nextIsBacklogSizeEnabled = !prevIsBacklogSizeEnabled;
      propagateChange("backlog_size", prevIsBacklogSizeEnabled ? 0 : backlogSize);
      return nextIsBacklogSizeEnabled;
    });
  };
  const handleColorChange = (color, _, hidePopover) => {
    hidePopover();
    propagateChange("color", color);
  };
  const handleChange = (event) => {
    const { name } = event.target;
    propagateChange(name, getValueFromInput(event.target));
  };
  const handleTimeZoneChange = (nextValue) => {
    propagateChange("time_zone", nextValue);
  };
  const element = /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "slack_notification_form.custom_message_help",
      components: {
        docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { text: t("slack_notification_form.docs_link_text"), page: "alerts#notifications" })
      }
    }
  ));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "color" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("slack_notification_form.configuration_color_label")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ColorLabel, { color: config.color }), /* @__PURE__ */ React.createElement("div", { style: { display: "inline-block", marginLeft: 15 } }, /* @__PURE__ */ React.createElement(
    ColorPickerPopover,
    {
      id: "color",
      color: config.color || "#f06292",
      placement: "right",
      triggerNode: /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall" }, t("slack_notification_form.change_color_button")),
      onChange: handleColorChange
    }
  ))), /* @__PURE__ */ React.createElement(HelpBlock, null, t("slack_notification_form.color_help"))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-webhookUrl",
      name: "webhook_url",
      label: t("slack_notification_details.webhook_url_label"),
      type: "text",
      bsStyle: validation.errors.webhook_url ? "error" : null,
      help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.webhook_url) == null ? void 0 : _b[0]) || t("slack_notification_form.webhook_url_help"),
      value: config.webhook_url || "",
      onChange: handleChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-channel",
      name: "channel",
      label: t("slack_notification_details.channel_label"),
      type: "text",
      bsStyle: validation.errors.channel ? "error" : null,
      help: ((_d = (_c = validation == null ? void 0 : validation.errors) == null ? void 0 : _c.channel) == null ? void 0 : _d[0]) || t("slack_notification_form.channel_help"),
      value: config.channel || "",
      onChange: handleChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-customMessage",
      name: "custom_message",
      label: t("slack_notification_form.custom_message_label"),
      type: "textarea",
      bsStyle: validation.errors.custom_message ? "error" : null,
      help: ((_f = (_e = validation == null ? void 0 : validation.errors) == null ? void 0 : _e.custom_message) == null ? void 0 : _f[0]) || element,
      value: config.custom_message || "",
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(
    EventProcedureCheckbox,
    {
      checked: config.include_event_procedure,
      onChange: handleChange,
      validation
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-time-zone",
      help: t("slack_notification_form.time_zone_help"),
      label: t("slack_notification_form.time_zone_label")
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        className: "timezone-select",
        name: "time_zone",
        value: config.time_zone,
        onChange: handleTimeZoneChange,
        clearable: false
      }
    )
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("slack_notification_form.backlog_limit_label")), /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Addon, null, /* @__PURE__ */ React.createElement(
    "input",
    {
      id: "toggle_backlog_size",
      type: "checkbox",
      checked: isBacklogSizeEnabled,
      onChange: toggleBacklogSize
    }
  )), /* @__PURE__ */ React.createElement(
    FormControl,
    {
      type: "number",
      id: "backlog_size",
      name: "backlog_size",
      onChange: handleBacklogSizeChange,
      value: backlogSize,
      min: "0",
      disabled: !isBacklogSizeEnabled
    }
  )), /* @__PURE__ */ React.createElement(HelpBlock, null, t("slack_notification_form.backlog_limit_help"))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-userName",
      name: "user_name",
      label: t("slack_notification_form.user_name_label"),
      type: "text",
      bsStyle: validation.errors.user_name ? "error" : null,
      help: ((_h = (_g = validation == null ? void 0 : validation.errors) == null ? void 0 : _g.user_name) == null ? void 0 : _h[0]) || t("slack_notification_form.user_name_help"),
      value: config.user_name || "",
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "include_title",
      name: "include_title",
      label: t("slack_notification_details.include_title_label"),
      bsStyle: validation.errors.include_title ? "error" : null,
      help: ((_j = (_i = validation == null ? void 0 : validation.errors) == null ? void 0 : _i.include_title) == null ? void 0 : _j[0]) || t("slack_notification_form.include_title_help"),
      type: "checkbox",
      checked: config.include_title,
      onChange: handleChange
    }
  )), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-notifyChannel",
      name: "notify_channel",
      label: t("slack_notification_details.notify_channel_label"),
      type: "checkbox",
      bsStyle: validation.errors.notify_channel ? "error" : null,
      help: ((_l = (_k = validation == null ? void 0 : validation.errors) == null ? void 0 : _k.notify_channel) == null ? void 0 : _l[0]) || t("slack_notification_form.notify_channel_help"),
      checked: (_m = config.notify_channel) != null ? _m : false,
      onChange: handleChange
    }
  )), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-notifyHere",
      name: "notify_here",
      label: t("slack_notification_form.notify_here_label"),
      type: "checkbox",
      bsStyle: validation.errors.notify_here ? "error" : null,
      help: ((_o = (_n = validation == null ? void 0 : validation.errors) == null ? void 0 : _n.notify_here) == null ? void 0 : _o[0]) || t("slack_notification_form.notify_here_help"),
      checked: (_p = config.notify_here) != null ? _p : false,
      onChange: handleChange
    }
  ))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-linkNames",
      name: "link_names",
      label: t("slack_notification_details.link_names_label"),
      type: "checkbox",
      bsStyle: validation.errors.link_names ? "error" : null,
      help: ((_r = (_q = validation == null ? void 0 : validation.errors) == null ? void 0 : _q.link_names) == null ? void 0 : _r[0]) || t("slack_notification_form.link_names_help"),
      checked: !!config.link_names,
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-iconUrl",
      name: "icon_url",
      label: t("slack_notification_form.icon_url_label"),
      type: "text",
      bsStyle: validation.errors.icon_url ? "error" : null,
      help: ((_t = (_s = validation == null ? void 0 : validation.errors) == null ? void 0 : _s.icon_url) == null ? void 0 : _t[0]) || t("slack_notification_form.icon_url_help"),
      value: config.icon_url || "",
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-iconEmoji",
      name: "icon_emoji",
      label: t("slack_notification_form.icon_emoji_label"),
      type: "text",
      bsStyle: validation.errors.icon_emoji ? "error" : null,
      help: ((_v = (_u = validation == null ? void 0 : validation.errors) == null ? void 0 : _u.icon_emoji) == null ? void 0 : _v[0]) || t("slack_notification_form.icon_emoji_help"),
      value: config.icon_emoji || "",
      onChange: handleChange
    }
  ));
};
export default SlackNotificationForm;
